/**
 * The people every Help & Tour shares: employees, the users they map to, and
 * the user groups those users belong to.
 *
 * One cast across all tours keeps a tour that searches for "Arun" on one screen
 * consistent with the award that names him on the next. Names, ids and emails
 * are invented for the demo — the `TOUR-` / `demo.sharesvision` markers make
 * that obvious at a glance and keep the data clearly non-production.
 */

export interface TourEmployee {
  id: number;
  employeeId: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  designation: string;
  company: string;
  country: string;
  jobGrade: string;
  employmentStatus: string;
  dateOfJoining: string;
}

export const TOUR_EMPLOYEES: readonly TourEmployee[] = Object.freeze([
  {
    id: 9001,
    employeeId: 'TOUR-EMP-001',
    fullName: 'Arun Kumar',
    firstName: 'Arun',
    lastName: 'Kumar',
    email: 'arun.kumar@demo.sharesvision.example',
    department: 'Finance',
    designation: 'Finance Manager',
    company: 'Northwind Holdings Pte Ltd',
    country: 'Singapore',
    jobGrade: 'M3',
    employmentStatus: 'Active',
    dateOfJoining: '2019-04-01',
  },
  {
    id: 9002,
    employeeId: 'TOUR-EMP-002',
    fullName: 'Priya Sharma',
    firstName: 'Priya',
    lastName: 'Sharma',
    email: 'priya.sharma@demo.sharesvision.example',
    department: 'Human Resources',
    designation: 'HR Business Partner',
    company: 'Northwind Holdings Pte Ltd',
    country: 'Singapore',
    jobGrade: 'M2',
    employmentStatus: 'Active',
    dateOfJoining: '2020-09-15',
  },
  {
    id: 9003,
    employeeId: 'TOUR-EMP-003',
    fullName: 'Daniel Tan',
    firstName: 'Daniel',
    lastName: 'Tan',
    email: 'daniel.tan@demo.sharesvision.example',
    department: 'Technology',
    designation: 'Engineering Lead',
    company: 'Northwind Holdings Pte Ltd',
    country: 'Singapore',
    jobGrade: 'M4',
    employmentStatus: 'Active',
    dateOfJoining: '2018-01-08',
  },
  {
    id: 9004,
    employeeId: 'TOUR-EMP-004',
    fullName: 'Mei Ling Chua',
    firstName: 'Mei Ling',
    lastName: 'Chua',
    email: 'meiling.chua@demo.sharesvision.example',
    department: 'Legal & Compliance',
    designation: 'Compliance Officer',
    company: 'Northwind Holdings Pte Ltd',
    country: 'Singapore',
    jobGrade: 'M3',
    employmentStatus: 'Active',
    dateOfJoining: '2021-06-21',
  },
  {
    id: 9005,
    employeeId: 'TOUR-EMP-005',
    fullName: 'Rajesh Menon',
    firstName: 'Rajesh',
    lastName: 'Menon',
    email: 'rajesh.menon@demo.sharesvision.example',
    department: 'Operations',
    designation: 'Operations Director',
    company: 'Northwind Capital Sdn Bhd',
    country: 'Malaysia',
    jobGrade: 'M5',
    employmentStatus: 'Active',
    dateOfJoining: '2017-03-13',
  },
  {
    id: 9006,
    employeeId: 'TOUR-EMP-006',
    fullName: 'Sarah Lim',
    firstName: 'Sarah',
    lastName: 'Lim',
    email: 'sarah.lim@demo.sharesvision.example',
    department: 'Finance',
    designation: 'Financial Controller',
    company: 'Northwind Holdings Pte Ltd',
    country: 'Singapore',
    jobGrade: 'M4',
    employmentStatus: 'Active',
    dateOfJoining: '2016-11-02',
  },
  {
    id: 9007,
    employeeId: 'TOUR-EMP-007',
    fullName: 'Kevin Wong',
    firstName: 'Kevin',
    lastName: 'Wong',
    email: 'kevin.wong@demo.sharesvision.example',
    department: 'Sales',
    designation: 'Regional Sales Manager',
    company: 'Northwind Capital Sdn Bhd',
    country: 'Malaysia',
    jobGrade: 'M3',
    employmentStatus: 'Resigned',
    dateOfJoining: '2019-08-19',
  },
  {
    id: 9008,
    employeeId: 'TOUR-EMP-008',
    fullName: 'Aisha Rahman',
    firstName: 'Aisha',
    lastName: 'Rahman',
    email: 'aisha.rahman@demo.sharesvision.example',
    department: 'Technology',
    designation: 'Product Manager',
    company: 'Northwind Holdings Pte Ltd',
    country: 'Singapore',
    jobGrade: 'M3',
    employmentStatus: 'Active',
    dateOfJoining: '2022-02-14',
  },
]);

/** The employee a tour lands on when a flow needs "the" record. */
export const TOUR_PRIMARY_EMPLOYEE = TOUR_EMPLOYEES[0];

export interface TourUserGroup {
  id: number;
  groupName: string;
  dataAccessLevels: string;
  secondarySelections: string | null;
  systemModuleIds: string;
  status: string;
  statusName: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
  userCount: number;
}

export const TOUR_USER_GROUPS: readonly TourUserGroup[] = Object.freeze([
  {
    id: 7101,
    groupName: 'Plan Administrators',
    dataAccessLevels: 'Company',
    secondarySelections: 'Northwind Holdings Pte Ltd',
    systemModuleIds: '[1,2,3,4]',
    status: 'Active',
    statusName: 'Active',
    createdBy: 'Sarah Lim',
    createdAt: '2024-01-15T09:20:00Z',
    updatedBy: null,
    updatedAt: null,
    userCount: 4,
  },
  {
    id: 7102,
    groupName: 'Finance Approvers',
    dataAccessLevels: 'Company',
    secondarySelections: 'Northwind Holdings Pte Ltd',
    systemModuleIds: '[2,3]',
    status: 'Active',
    statusName: 'Active',
    createdBy: 'Sarah Lim',
    createdAt: '2024-02-02T10:05:00Z',
    updatedBy: 'Arun Kumar',
    updatedAt: '2024-05-11T14:41:00Z',
    userCount: 3,
  },
  {
    id: 7103,
    groupName: 'HR Operations',
    dataAccessLevels: 'Department',
    secondarySelections: 'Human Resources',
    systemModuleIds: '[1]',
    status: 'Active',
    statusName: 'Active',
    createdBy: 'Priya Sharma',
    createdAt: '2024-03-18T08:30:00Z',
    updatedBy: null,
    updatedAt: null,
    userCount: 6,
  },
  {
    id: 7104,
    groupName: 'Plan Participants',
    dataAccessLevels: 'Self',
    secondarySelections: null,
    systemModuleIds: '[5]',
    status: 'Active',
    statusName: 'Active',
    createdBy: 'Sarah Lim',
    createdAt: '2024-01-15T09:25:00Z',
    updatedBy: null,
    updatedAt: null,
    userCount: 128,
  },
]);

export interface TourUser {
  id: number;
  userId: number;
  employeeId: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  userGroupId: number;
  userGroupName: string;
  employmentStatus: string;
  status: string;
  statusName: string;
  statusId: number;
  lastLogin: string;
  department: string;
  designation: string;
  company: string;
  country: string;
  createdBy: string;
  createdAt: string;
}

const userGroupFor = (index: number): TourUserGroup =>
  TOUR_USER_GROUPS[index % TOUR_USER_GROUPS.length];

/**
 * Users are derived from the employees so the two lists always agree — a tour
 * that finds "Daniel Tan" in User Management sees the same id and email that
 * his award record carries.
 */
export const TOUR_USERS: readonly TourUser[] = Object.freeze(
  TOUR_EMPLOYEES.map((employee, index) => {
    const group = userGroupFor(index);
    const isActive = employee.employmentStatus === 'Active';
    return {
      id: employee.id,
      userId: employee.id,
      employeeId: employee.employeeId,
      fullName: employee.fullName,
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      userGroupId: group.id,
      userGroupName: group.groupName,
      employmentStatus: employee.employmentStatus,
      status: isActive ? 'Active' : 'Inactive',
      statusName: isActive ? 'Active' : 'Inactive',
      statusId: isActive ? 1 : 2,
      lastLogin: isActive
        ? `2025-09-${String(8 + index).padStart(2, '0')}T08:45:00Z`
        : '2025-04-02T11:12:00Z',
      department: employee.department,
      designation: employee.designation,
      company: employee.company,
      country: employee.country,
      createdBy: 'Sarah Lim',
      createdAt: '2024-01-20T09:00:00Z',
    };
  })
);

export const TOUR_PRIMARY_USER = TOUR_USERS[0];
